import React, { useState, useEffect } from "react";
import styles from "./meeting.module.scss";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import cx from "classnames";
import { useHistory } from "react-router-dom";
import paperfliteLogo from "images/paperflite_logo.svg";
import { fetchUserAccount, fetchUserProfile } from "actions/users";
import Industry, { IndustryDetails } from "./components/industry";
import Shares, { SharesDetails } from "./components/shares";
import DealRoom from "./components/dealroom";
import RecommendedContent, {
  RecommendedContentDetails,
} from "./components/recommendedContent";
import Button from "components/button";
import EventCountdown from "components/eventCountdown";
import { fetchDeals, fetchSimilarDeals } from "actions/meeting";
import { Image } from "react-bootstrap";
import { trackAdoptionMetricsForMeetingPrepClickedEvent } from "actions/adoption";
import Truncate from "react-truncate";
import dateformat from 'dateformat';
import AuthService from "services/auth.service";

const noDealsImg = "/images/meeting/no_deals.png";

export default function Meeting() {
  const dispatch = useDispatch();

  const profile = useSelector(
    (state) => _.get(state, "users.profile.item") || null
  );

  const userId = useSelector(
    (state) => _.get(state, "users.profile.item.id") || null
  );

  const fetchingDeals = useSelector(
    (state) => _.get(state, "meeting.fetchingDeals") || false
  );
  const fetchedDeals = useSelector(
    (state) => _.get(state, "meeting.fetchedDeals") || false
  );

  const meetingDetails = useSelector(
    (state) => _.get(state, "selection.meetingDetails") || {}
  );

  const deals = useSelector(
    (state) => _.get(state, "selection.dealDetails") || {}
  );

  const similarDeals = useSelector(
    (state) => _.get(state, "meeting.similarDeals.deals") || {}
  );

  const [showDetails, setShowDetails] = useState(false);
  const [section, setSection] = useState("");
  const [sectionHeader, setSectionHeader] = useState("");
  const [isMeetingExpired, setIsMeetingExpired] = useState(false);
  const [isMeetingInProgress, setIsMeetingInProgress] = useState(false);

  useEffect(() => {
    dispatch(trackAdoptionMetricsForMeetingPrepClickedEvent("Meeting Prep Clicked"));
    if (_.size(profile) === 0) {
      dispatch(fetchUserAccount());
      dispatch(fetchUserProfile());
    }
  }, []);

  useEffect(() => {
    if(_.size(deals) > 0) {
      let dealId = null;
      if(deals[0].integrationType === "hubspot"){
        dealId = deals[0].opportunityId;
      } else {
        dealId = deals[0].id;
      }
      dispatch(fetchSimilarDeals({ dealId: dealId }));
    }
  },[deals])

  useEffect(() => {
  if (_.size(meetingDetails) > 0 && userId) {
    setShowDetails(false);
    setSection("");

    const meetingTime = getMeetingTime();

    if (meetingTime) {
      const meetingEndTime = new Date(meetingTime.getTime() + 60 * 60 * 1000);
      const now = new Date();

      if (now >= meetingTime && now <= meetingEndTime) {
        setIsMeetingInProgress(true);
        setIsMeetingExpired(false);
      } else if (now > meetingEndTime) {
        setIsMeetingExpired(true);
        setIsMeetingInProgress(false);
      } else {
        setIsMeetingExpired(false);
        setIsMeetingInProgress(false);
        }
      }
    } else {
      setIsMeetingExpired(false);
      setIsMeetingInProgress(false);
    }
  }, [meetingDetails, userId]);

  const containerRef = React.useRef(null);

  const getMeetingTime = () => {
    if (_.isEmpty(meetingDetails) || !meetingDetails.meetingDateAndTime) {
      return null;
    }

    try {
      const meetingDateAndTimeDetails = meetingDetails.meetingDateAndTime;
      const meetingDateAndTime = meetingDateAndTimeDetails.split("–");
      if (meetingDateAndTime.length < 2) return null;

      const meetingDate = meetingDateAndTime[0].trim();
      const meetingTime = meetingDateAndTime[1].trim();

      const ampmMatch = meetingTime.match(/(am|pm)$/i);
      const ampm = ampmMatch ? ampmMatch[1] : "pm";

      const year = new Date().getFullYear();
      const dateMatch = meetingDate.match(/(\w+),\s*(\w+)\s*(\d+)\s*(\d+):(\d+)/);

      if (!dateMatch) return null;

      const [, dayName, month, day, hours, minutes] = dateMatch;

      let hour24 = parseInt(hours);
      if (ampm.toLowerCase() === "pm" && hour24 !== 12) hour24 += 12;
      else if (ampm.toLowerCase() === "am" && hour24 === 12) hour24 = 0;

      const dateString = `${month} ${day}, ${year} ${hour24
        .toString()
        .padStart(2, "0")}:${minutes}:00`;

      const parsedDate = new Date(dateString);
      if (isNaN(parsedDate.getTime())) {
        console.warn("Invalid meeting date string:", meetingDetails.meetingDateAndTime);
        return null;
      }

      return parsedDate;
      } catch (e) {
      console.error("Error parsing meeting time:", e);
      return null;
      }
    };


  const handleClick = (section) => {
    setShowDetails(true);
    setSection(section);

    switch (section) {
      case "industry":
        setSectionHeader("Industry & Key stakeholders");
        break;
      case "recommendedContent":
        setSectionHeader("Recommended Content");
        break;
      case "shares":
        setSectionHeader("Snapshot");
        break;
      default:
        setSectionHeader("");
    }
  };

  const handleBackClick = () => {
    setShowDetails(false);
    setSection("");
  };

  return (
    <div
      ref={containerRef}
      id="meeting-prep-container"
      className={cx(styles.container, {
        [styles.hideScrollContainer]: showDetails,
      })}
    >
      <div
        className={cx(styles.headerContainer, {
          [styles.showDetailsHeader]: showDetails,
        })}
      >

      {showDetails && <div>
        <Button
              text={sectionHeader}
              className={styles.backButton}
              handleButtonClick={handleBackClick}
              icon="paperflite-left-arrow"
            ></Button>
      </div>}

      </div>

      <div className={cx(styles.bodyContainer, {})}>
        <div
          className={cx(styles.mainContainer, {
            [styles.slideOut]: showDetails,
          })}
        >
          <div className={cx(styles.titleContainer, {})}>
            <div className={styles.title}>
              Ready to go, {_.get(profile, "firstName", "")}?
            </div>
            <div className={styles.subTitle}>
            {isMeetingInProgress ? <span>The meeting is in progress.</span> : isMeetingExpired ? <span>The meeting has already ended.</span> :
              !_.isEmpty(meetingDetails) && getMeetingTime() ? <span>your next call is starting in {" "}
               <EventCountdown targetDate={getMeetingTime()} />
               ..</span> 
            : <span>No upcoming calls</span>}
            </div>
          </div>

          <div className={styles.contentContainer}>
            {!fetchingDeals && fetchedDeals && _.size(deals) === 0 && (
              <div className={styles.emptyMessage}>
                <Image src={noDealsImg} alt="No deals mapped for this meeting" height={150} width={150}/>
                <div>No deals mapped for this meeting</div>
              </div>
            )}

            {(
              <>
              <div className={styles.industryContainer}>
                <Industry handleClick={handleClick} />
              </div>

              <div className={styles.dealroomContainer}>
                <DealRoom handleClick={handleClick} />
              </div>

              <div className={styles.knowMoreContainer}>
                <Shares handleClick={handleClick} />
              </div>

            {_.size(similarDeals) > 0 &&  <div className={styles.dealsContainer}>
                <div className={styles.header}>
                  Deals you've closed recently
                  </div>
                  <div className={styles.dealsList}>
                    {_.map(similarDeals, (deal, index) => (
                      <div key={index} className={styles.dealDetails} >
                        <div className={styles.name}>
                          <Truncate lines={1}> {_.get(deal, "name", "")} </Truncate>
                        </div>
                        <div className={styles.dealInfo}>
                          <span className={styles.value}>${_.get(deal, "amount", "")},</span>
                          <span className={styles.date}>Closed Date : {dateformat(_.get(deal, "closedDate", ""), "dS mmm'yy")}</span>
                        </div>
                        </div>
                    ))}
                  </div>
                </div>}

              <div className={styles.recommendedContentContainer}>
                <RecommendedContent handleClick={handleClick} />
              </div>
              </>
            )}
          </div>
        </div>

        <div
          className={cx(styles.detailsContainer, {
            [styles.slideIn]: showDetails,
          })}
        >
          <DetailsContainer section={section} />
        </div>
      </div>
    </div>
  );
}

const DetailsContainer = ({ section }) => {
  const dispatch = useDispatch();

  const recommendedAssets = useSelector(
    (state) => _.get(state, "assets.recommendedAssets.items") || []
  );

  const frequentlySharedAssets = useSelector(
    (state) => _.get(state, "assets.frequentlyShared.items") || []
  );
  const shares = useSelector(
    (state) => _.get(state, "conversations.items") || []
  );

  const allContent = [...recommendedAssets, ...frequentlySharedAssets];

  if (section === "industry") {
    return <IndustryDetails />;
  }

  if (section === "recommendedContent") {
    return (
      <RecommendedContentDetails
        allContent={allContent}
      />
    );
  }

  if (section === "shares") {
    return <SharesDetails />;
  }

  return null;
};
